import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Layout, message, Button } from 'antd';
import Icon, { MoreOutlined } from '@ant-design/icons';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { cloneDeep, omit, lowerCase } from 'lodash-es';
import uuid from '../../utils/uuid';
import classNames from 'classnames';
import { BehaviorSubject, from } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { useMediaQuery } from 'react-responsive';
import { getForm, saveUpdates } from '../../store/actions/formActions';

import StagingArea from './StagingArea';
import PropertiesPanel from './PropertiesPanel';
import QuestionsPanel from './QuestionsPanel';
import PreviewForm from '../PreviewForm';
import { useTranslation } from 'react-i18next';

import { ITEMS, ADVANCED_ITEMS, questionLabelTexts, ToolBarHeaderMap } from '../../constants';
import { publishService } from '../../utils/publish-subject';

import { removeDuplicateError } from '../../store/actions/errorActions';
import { showUpselling } from '../../store/actions/paymentActions';
import { hasFeature } from '../../utils/restrictions';
import { checkForAdvancedFields, insert } from '../../utils/helpers';
import { formByIdSelector, formQuestionsSelector } from '../../store/selectors/formSelector';
import { useParams, Link } from 'react-router-dom';
import StagingAreaWithSections from './StagingAreaWithSections';
// import StagingAreaWithSections from './StagingAreaWithSections_';
import { ExternalLink } from 'react-feather';
import { ADVANCED_QUESTION_TYPES } from '../../../../server/src/common/utils/constants';
import LogicsPanel from './LogicsPanel';

const { Content } = Layout;

const BuilderContext = React.createContext(null);

export const useBuilderContext = () => {
  const context = React.useContext(BuilderContext);
  if (context === undefined) {
    throw new Error('useBuilderContext must be used within a BuilderContext');
  }
  return context;
};

const Build = ({
  previewMode,
  openQuestionPanel,
  setOpenQuestionPanel,
  openPropertiesPanel,
  setOpenPropertiesPanel,
  openLogicsPanel,
  setOpenLogicsPanel,
  selectedQuestionId,
  setSelectedQuestionId,
  setUnpublishedChanges,
}) => {
  const { t } = useTranslation(['builder']);

  const params = useParams();
  const dispatch = useDispatch();
  const { me } = useSelector((state) => state.auth);

  const isWidthScreen = useMediaQuery({ minWidth: 1200 });
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });
  const form = useSelector(formByIdSelector(params.id));
  const formQuestions = formQuestionsSelector(form);

  const [questions, setQuestions] = useState(formQuestions);

  const contextValue = useMemo(() => ({ form }), [form]);

  useEffect(() => {
    return () => {
      dispatch(getForm(form.id));
    };
  }, [dispatch, form.id]);

  useEffect(() => {
    setQuestions(formQuestions);
  }, [formQuestions]);

  const hasSections = questions?.some((el) => el.type === 'SECTION');

  const sectionMap = questions?.reduce(
    (acc, curr, index) => {
      if (curr.type === 'SECTION') {
        // const sectionKey = `section-${Object.keys(acc).length}`;
        const sectionKey = curr.questionId;
        acc[sectionKey] = {
          ...curr,
          label:
            curr?.label ||
            `${t('builder:propertiesPanel.section.name')} ${Object.keys(acc).length + 1}`,
          questions: [],
        };
      } else {
        const lastSectionKey = Object.keys(acc)[Object.keys(acc).length - 1];
        acc[lastSectionKey]?.questions.push({ ...curr, sectionId: lastSectionKey });
      }
      return acc;
    },
    // eslint-disable-next-line no-constant-condition
    hasSections
      ? {}
      : {
          'section-0': {
            label: `${t('builder:propertiesPanel.section.name')} 0`,
            // id: 'section-0',
            questionId: 'section-0',
            questions: [],
          },
        },
  );

  const sectionOrder = Object.keys(sectionMap);

  // API polling
  const shouldPoll = useRef(false);
  const properties$ = React.useRef(new BehaviorSubject(''));

  useEffect(() => {
    const updateProperties$ = from(properties$.current).pipe(
      filter((val) => !!val),
      debounceTime(400),
    );

    const subscription = updateProperties$.subscribe(({ formId, state }) => {
      if (formId && state) {
        dispatch(saveUpdates(formId, state)).catch((err) => {});
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [properties$, dispatch]);

  useEffect(() => {
    const sub$ = publishService.getMessage().subscribe((value) => {
      setUnpublishedChanges(value);
    });
    return () => {
      sub$.unsubscribe();
    };
  }, [setUnpublishedChanges]);

  const clearSelection = (e) => {};

  const selectQuestion = (id) => {
    setSelectedQuestionId(id);
    if (openLogicsPanel) setOpenLogicsPanel(false);
    setOpenPropertiesPanel(true);
  };

  const onDragStart = () => {
    shouldPoll.current = false;

    if (isMobile) {
      setOpenQuestionPanel(false);
    }
  };

  // a little function to help us with reordering the result
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const copy = (source, destination, droppableSource, droppableDestination) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const item = sourceClone[droppableSource.index];
    const { initialValue } = item;
    destClone.splice(droppableDestination.index, 0, {
      ...initialValue,
      questionId: uuid(),
      label: questionLabelTexts[form.language][item.type],
    });
    return destClone;
  };

  // const onDragEnd = (result) => {
  //   const { destination, source } = result;
  //   // dropped outside the list
  //   if (!destination) {
  //     return;
  //   }
  //   switch (source.droppableId) {
  //     case destination.droppableId: {
  //       const currentQuestion = questions[source.index];
  //       // section can't be moved to the top
  //       if (currentQuestion.type === 'SECTION' && destination.index === 0) {
  //         return message.error(
  //           t('builder:sectionTopError', 'Section can not be the first item in the form'),
  //         );
  //       }
  //       const newState = reorder(questions, source.index, destination.index);
  //       shouldPoll.current = true;
  //       setQuestions(newState);
  //       dispatch(saveUpdates(form.id, newState)).catch((err) => {});
  //       break;
  //     }
  //     case 'ITEMS': {
  //       const item = Array.from(ITEMS)[source.index];
  //       if (item.type === 'FILE' && !hasFeature(me.plan, 'fileUpload'))
  //         return dispatch(showUpselling('fileupload'));
  //       let newState;
  //       if (!hasSections) {
  //         // if there are questions but no sections. Add 1 section first.
  //         const newSection = {
  //           ...ITEMS.find((el) => el.type === 'SECTION').initialValue,
  //           questionId: uuid(),
  //         };
  //         const questionClone = Array.from(questions);
  //         questionClone.unshift(newSection);
  //         newState = copy(ITEMS, questionClone, source, destination);
  //       } else {
  //         newState = copy(ITEMS, questions, source, destination);
  //       }

  //       shouldPoll.current = true;
  //       setQuestions(newState);
  //       dispatch(saveUpdates(form.id, newState)).catch((err) => {});

  //       if (selectedQuestionId) setSelectedQuestionId(newState[destination.index].questionId);
  //       break;
  //     }
  //     default:
  //       break;
  //   }
  // };

  const buildQuestionArrayFromSections = (sectionMap, sectionOrder) => {
    return sectionOrder.reduce((acc, curr, index) => {
      const section = sectionMap[curr];
      const sectionObj = questions.find((el) => el.questionId === section?.questionId);
      if (sectionObj) acc.push(sectionObj);
      section.questions.forEach((question) => acc.push(question));
      return acc;
    }, []);
  };

  /**
   *
   * @param {DropResult} result
   * @returns
   */
  const onDragEndWithSections = (result) => {
    const { destination, source } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    // did not move anywhere - can bail early
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    // Add questions to form
    if (source.droppableId === 'ITEMS' || source.droppableId === 'ADVANCED_ITEMS') {
      const sourceItems = source.droppableId === 'ITEMS' ? ITEMS : ADVANCED_ITEMS;
      const item = Array.from(sourceItems)[source.index];
      if (source.droppableId === 'ADVANCED_ITEMS' && !hasFeature(me.plan, 'advancedFields'))
        return dispatch(showUpselling(lowerCase(item.content?.replaceAll(/\s/g, ''))));

      if (item.type === 'PAYMENT' && questions.find((el) => el.type === 'PAYMENT'))
        return message.error(t('builder:errors.onlyOnePaymentAllowed'));

      let newState;
      let newQuestionId;
      if (!hasSections) {
        // if there are questions but no sections. Add 1 section first.
        const newSection = {
          ...ADVANCED_ITEMS.find((el) => el.type === 'SECTION').initialValue,
          questionId: uuid(),
        };
        const newSectionMap = {
          [newSection.questionId]: {
            label: `${t('builder:propertiesPanel.section.name')} 1`,
            // id: newSection.questionId,
            ...newSection,
            questions: Array.from(questions),
          },
        };
        const sectionQuestions = [newSection, ...newSectionMap[newSection.questionId].questions];
        newSectionMap[newSection.questionId].questions = copy(
          sourceItems,
          sectionQuestions,
          source,
          {
            index: destination.index + 1,
          },
        );
        newState = buildQuestionArrayFromSections(newSectionMap, [newSection.questionId]);
        newQuestionId =
          newSectionMap[newSection.questionId].questions[destination.index].questionId;
      } else {
        const cloneSectionMap = cloneDeep(sectionMap);
        const sectionQuestions = cloneSectionMap[destination.droppableId].questions;
        cloneSectionMap[destination.droppableId].questions = copy(
          sourceItems,
          sectionQuestions,
          source,
          destination,
        );
        newState = buildQuestionArrayFromSections(cloneSectionMap, sectionOrder);
        newQuestionId =
          cloneSectionMap[destination.droppableId].questions[destination.index].questionId;
      }
      shouldPoll.current = true;
      setQuestions(newState);

      dispatch(saveUpdates(form.id, newState)).catch((err) => {});

      if (selectedQuestionId) setSelectedQuestionId(newQuestionId);
      return;
    }

    if (result.type === 'SECTION') {
      // reorder sections
      const currentOrder = Array.from(sectionOrder);
      const newOrder = reorder(currentOrder, source.index, destination.index);
      const newState = buildQuestionArrayFromSections(sectionMap, newOrder);

      shouldPoll.current = true;
      setQuestions(newState);
      dispatch(saveUpdates(form.id, newState)).catch((err) => {});
      return;
    }

    const cloneSectionMap = cloneDeep(sectionMap);
    const current = cloneSectionMap[source.droppableId];
    const next = cloneSectionMap[destination.droppableId];
    const target = current.questions[source.index];

    // moving in the same section
    if (source.droppableId === destination.droppableId) {
      const sectionQuestions = current.questions;
      current.questions = reorder(sectionQuestions, source.index, destination.index);
      const newState = buildQuestionArrayFromSections(cloneSectionMap, sectionOrder);

      shouldPoll.current = true;
      setQuestions(newState);
      dispatch(saveUpdates(form.id, newState)).catch((err) => {});
      return;
    }

    // moving to a different section
    // remove from original
    current.questions.splice(source.index, 1);
    // insert into next
    next.questions.splice(destination.index, 0, target);

    const newState = buildQuestionArrayFromSections(cloneSectionMap, sectionOrder);

    shouldPoll.current = true;
    setQuestions(newState);
    dispatch(saveUpdates(form.id, newState)).catch((err) => {});
  };

  // Fired when user clicks on question item.
  const addFormElement = (item) => {
    if (!item) return;
    if (ADVANCED_QUESTION_TYPES.includes(item.type) && !hasFeature(me.plan, 'advancedFields'))
      return dispatch(showUpselling(lowerCase(item.content?.replaceAll(/\s/g, ''))));

    if (item.type === 'PAYMENT' && questions.find((el) => el.type === 'PAYMENT'))
      return message.error(t('builder:errors.onlyOnePaymentAllowed'));

    let newState = Array.from(questions);
    if (!hasSections) {
      // if there are questions but no sections. Add 1 section first.
      const newSection = {
        ...ADVANCED_ITEMS.find((el) => el.type === 'SECTION').initialValue,
        questionId: uuid(),
      };
      newState.unshift(newSection);
    }

    const newId = uuid();
    newState.push({
      ...item.initialValue,
      questionId: newId,
      label: questionLabelTexts[form.language][item.type],
    });

    setQuestions(newState);
    dispatch(saveUpdates(form.id, newState)).catch((err) => {});

    if (isMobile) {
      setOpenQuestionPanel(false);
    } else if (selectedQuestionId) {
      setSelectedQuestionId(newId);
      setOpenPropertiesPanel(true);
    }
    const stagingEl = document.querySelector('.preview-container');
    if (!stagingEl) return;
    setTimeout(() => {
      stagingEl.scrollTo({
        top: stagingEl.scrollHeight,
        behavior: 'smooth',
      });
    });
  };

  const deleteFormElement = (id) => {
    let newState;
    let removeId = id;
    let defaultSectionId = null;

    // Check if the element to be deleted is a section
    const sectionIndex = sectionOrder.findIndex((el) => el === id);

    // Handle special cases for sections
    if (sectionIndex !== -1) {
      // If it's the first section, remove the second section instead
      if (sectionIndex === 0) {
        removeId = sectionOrder[1];
      }
      // If there are only two sections, set the first one as default section for deletion
      if (sectionOrder.length === 2) {
        defaultSectionId = sectionOrder[0];
      }
    }

    // Remove the section and update related rules
    if (sectionIndex !== -1) {
      newState = questions.reduce((acc, curr) => {
        // Skip the section to be removed and the default section (if applicable)
        if (curr.questionId === removeId || curr.questionId === defaultSectionId) return acc;

        if (curr.type === 'SECTION') {
          // Update jumpToSection for other sections
          acc.push({
            ...curr,
            jumpToSection: [removeId, defaultSectionId].includes(curr?.jumpToSection)
              ? ''
              : curr?.jumpToSection,
          });
        } else {
          // Update rules for questions using a single reduce function
          const updatedRules = curr.rules?.reduce((acc, rule) => {
            if (
              curr.type === 'OPTION' &&
              (rule.jumpToSection === removeId || rule.jumpToSection === defaultSectionId)
            ) {
              // Reset logic for OPTION type
              acc.push({ ...rule, jumpToSection: '' });
            } else if (rule.jumpToSection !== removeId && rule.jumpToSection !== defaultSectionId) {
              // Keep the rule if it doesn't match the sections to be removed
              acc.push(rule);
            }
            // Skip adding the rule if it should be deleted
            return acc;
          }, []);

          acc.push({ ...curr, rules: updatedRules });
        }
        return acc;
      }, []);
    } else {
      // If it's not a section, simply filter out the question
      newState = questions.filter((el) => el.questionId !== removeId);
    }

    // Update state and perform cleanup
    shouldPoll.current = true;
    setQuestions(newState);
    removeDuplicateError(dispatch, id);

    // Close properties panel and clear selection after a short delay
    setTimeout(() => {
      setOpenPropertiesPanel(false);
      setSelectedQuestionId(null);
    });

    // Save the updated form
    dispatch(saveUpdates(form.id, newState)).catch((err) => {
      // Handle error if needed
    });
  };

  // const mergeWithAboveSection = (sectionId) => {
  //   const sectionIndex = sectionOrder.findIndex((el) => el === sectionId);
  //   // abort if it is the first section or there is no section
  //   if (sectionIndex <= 0) return;

  //   const newState = questions.filter((el) => el.questionId !== sectionId);
  //   setQuestions(newState);
  //   dispatch(saveUpdates(form.id, newState)).catch((err) => {});
  // };

  const copyFormElement = (id) => {
    const cloneElIndex = questions.findIndex((el) => el.questionId === id);
    if (cloneElIndex === -1) return;

    const newEl = cloneDeep(questions[cloneElIndex]);
    // prevent duplicating payment field
    if (newEl.type === 'PAYMENT') {
      return message.error(t('builder:errors.onlyOnePaymentAllowed'));
    }

    newEl.questionId = uuid();
    const newItems = [newEl];
    let insertAt = cloneElIndex + 1;

    if (newEl.type === 'SECTION') {
      const sectionQuestions = sectionMap[questions[cloneElIndex].questionId].questions;
      sectionQuestions?.forEach((question) => {
        const newQuestion = cloneDeep(question);
        newQuestion.questionId = uuid();
        newItems.push(newQuestion);
      });
      insertAt = cloneElIndex + sectionQuestions.length + 1;
    }

    const newState = insert(questions, insertAt, newItems);
    setQuestions(newState);
    dispatch(saveUpdates(form.id, newState)).catch((err) => {});
    handlePropertiesClose();
    // scroll to the new element

    setTimeout(() => {
      const [newElinDom] = document.getElementsByClassName(newEl.questionId) || [];
      if (!newElinDom) return;
      newElinDom.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
  };

  const updateProperties = (newQuestion) => {
    if (newQuestion) {
      const newState = questions.map((question) => {
        return question.questionId === selectedQuestion?.questionId
          ? { ...question, ...newQuestion }
          : question;
      });

      setQuestions(newState);
      properties$.current.next({ formId: form.id, state: newState });
    }
  };

  const handlePropertiesClose = () => {
    setOpenPropertiesPanel(false);
    setSelectedQuestionId(null);
  };

  const handleLogicsClose = () => {
    setOpenLogicsPanel(false);
    if (!openPropertiesPanel) setSelectedQuestionId(null);
  };

  const selectedQuestion =
    selectedQuestionId && questions.find((el) => el.questionId === selectedQuestionId);

  const formHasAdvancedFields = checkForAdvancedFields(form.formQuestions);
  const planHasAdvancedFields = hasFeature(me.plan, 'advancedFields');

  const showDisabledFormAlert = formHasAdvancedFields && !planHasAdvancedFields;

  return (
    <Layout
      style={{
        transition: 'padding 0.3s cubic-bezier(0.7, 0.3, 0.1, 1)',
        paddingRight: isWidthScreen && openPropertiesPanel && 350,
        paddingLeft: (isDesktop || isTablet) && openQuestionPanel && 250,
        willChange: 'padding',
      }}
      onMouseDown={clearSelection}
    >
      {showDisabledFormAlert && (
        <div
          style={{
            position: 'sticky',
            zIndex: 100,
            backgroundColor: '#F4F5F6',
            // padding: '16px 20px',
          }}
        >
          <Alert
            message={t(
              'builder:advancedFieldsDisabled',
              'Form is disabled because it contains advanced fields. Either remove advanced fields or upgrade your plan.',
            )}
            type="error"
            showIcon
            action={
              <Link to="/billing">
                <Button type="primary">
                  {t('builder:upgradePlan', 'Upgrade Plan')}
                  <Icon viewBox="0 0 15 15">
                    <ExternalLink size="1rem" />
                  </Icon>
                </Button>
              </Link>
            }
          />
        </div>
      )}
      <BuilderContext.Provider value={contextValue}>
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEndWithSections}>
          <QuestionsPanel
            addFormElement={addFormElement}
            visible={openQuestionPanel}
            setVisible={setOpenQuestionPanel}
            previewMode={previewMode}
          />
          <Content
            className={classNames('preview-container', {
              'preview-mode': previewMode,
            })}
          >
            {previewMode ? (
              <PreviewForm preview={previewMode} form={form} />
            ) : (
              <>
                <StagingAreaWithSections
                  questions={questions}
                  sectionMap={sectionMap}
                  sectionOrder={sectionOrder}
                  selectedQuestionId={selectedQuestionId}
                  selectQuestion={selectQuestion}
                  deleteElement={deleteFormElement}
                  cloneElement={copyFormElement}
                  // mergeWithAboveSection={mergeWithAboveSection}
                  openProperites={setOpenPropertiesPanel}
                  openLogics={(id) => {
                    setSelectedQuestionId(id);
                    setOpenLogicsPanel(true);
                  }}
                  formLanguage={form.language}
                  planHasAdvancedFields={planHasAdvancedFields}
                />
              </>
            )}
          </Content>
          <PropertiesPanel
            questions={questions}
            selectedQuestion={selectedQuestion}
            updateProperties={updateProperties}
            visible={openPropertiesPanel}
            setVisible={handlePropertiesClose}
            deleteElement={deleteFormElement}
            cloneElement={copyFormElement}
            openLogics={(id) => {
              setSelectedQuestionId(id);
              setOpenLogicsPanel(true);
            }}
          />
          <LogicsPanel
            questions={questions}
            selectedQuestion={selectedQuestion}
            updateProperties={updateProperties}
            visible={openLogicsPanel}
            setVisible={handleLogicsClose}
            sectionOrder={sectionOrder}
          />
        </DragDropContext>
      </BuilderContext.Provider>
    </Layout>
  );
};

export default Build;
